import { computed, ref } from 'vue'
import type { LangCode } from '@/types/config'
import { useConstConfigV2 } from '@/composables/useConstConfigV2'
import { getCurrentLanguage, switchLanguage } from '@/i18n'
import { getWebpUrl } from '@/utils/webpAssets'

export interface LangItem {
  img: string
  name: string
  value: LangCode
}

export type SupportLangsConfig = Record<LangCode | 'status', boolean>

export const ALL_LANGUAGES: LangItem[] = [
  { img: getWebpUrl('imgs/common/language/FlagZh.webp'), name: '简体中文', value: 'zh-CN' },
  { img: getWebpUrl('imgs/common/language/FlagEn.webp'), name: 'English', value: 'en-US' },
  { img: getWebpUrl('imgs/common/language/FlagVi.webp'), name: 'Tiếng Việt', value: 'vi-VN' },
  { img: getWebpUrl('imgs/common/language/FlagHi.webp'), name: 'हिन्दी', value: 'hi-IN' },
  { img: getWebpUrl('imgs/common/language/FlagTa.webp'), name: 'தமிழ்', value: 'ta-IN' },
  { img: getWebpUrl('imgs/common/language/FlagMs.webp'), name: 'Bahasa Melayu', value: 'ms-MY' },
  { img: getWebpUrl('imgs/common/language/FlagPt.webp'), name: 'Português', value: 'pt-BR' }
]

let initialized = false
const supportLangs = ref<SupportLangsConfig | null>(null)
const blockedLangs = ref<LangCode[]>(['vi-VN'])

export const useSupportLanguages = () => {
  const { getSupportLangs } = useConstConfigV2()

  const currentLangCode = computed(() => getCurrentLanguage())

  const visibleLangList = computed<LangItem[]>(() => {
    if (!supportLangs.value) return []

    return ALL_LANGUAGES.filter(
      (lang) => supportLangs.value?.[lang.value] && !blockedLangs.value.includes(lang.value)
    )
  })

  const currentLang = computed<LangItem | undefined>(() => {
    return ALL_LANGUAGES.find((item) => item.value === currentLangCode.value)
  })

  const isMultiLangEnabled = computed(() => {
    return supportLangs.value?.status ?? false
  })

  const setLang = async (value: LangCode) => {
    await switchLanguage(value)
  }

  const initLanguages = async (): Promise<SupportLangsConfig> => {
    const config = (await getSupportLangs()) as SupportLangsConfig
    supportLangs.value = config

    if (!initialized) {
      initialized = true
      await autoSwitchLanguageIfNeeded(config)
    }

    return config
  }

  const autoSwitchLanguageIfNeeded = async (config: SupportLangsConfig) => {
    const defaultLang = ((import.meta.env.VITE_DEFAULT_LANG as LangCode) || 'en-US') as LangCode

    if (!config.status) {
      if (currentLangCode.value !== defaultLang) {
        await setLang(defaultLang)
      }
      return
    }

    const currentLangSupported =
      config[currentLangCode.value] && !blockedLangs.value.includes(currentLangCode.value)

    if (currentLangSupported) return

    const firstAvailable = ALL_LANGUAGES.find(
      (lang) => config[lang.value] && !blockedLangs.value.includes(lang.value)
    )

    if (firstAvailable) {
      await setLang(firstAvailable.value)
      return
    }

    await setLang(defaultLang)
  }

  return {
    supportLangs,
    blockedLangs,
    visibleLangList,
    currentLang,
    currentLangCode,
    isMultiLangEnabled,
    initLanguages,
    setLang
  }
}
